// "Snap to paths" on Import files: a planned hike whose file was drawn by hand (or exported
// without turns) is matched onto the path network day by day, so it keeps the author's line
// but gets the turns and surfaces a planner-routed segment has. PRO only, off unless ticked.
// Matching runs as soon as the box is ticked, so the preview map can show the original line
// and the snapped one side by side; the save then reuses those matches. Same rules and event
// as the apps' import (ImportSnap on iOS / Android).

import { LineStringFeature } from '../models/LineStringFeature';
import { anchorToMarkers, type MapMatchResult } from '../models/mapMatch';
import { lineStringSaveAttributes } from '../models/lineStringSaveAttributes';
import { cumulativeDistancesMeters } from '../models/turnInstructions';
import { matchWithoutDetours } from '../models/mapMatchDetours';
import type { ImportDay as HikeDay } from '../helpers/gpxHikeImport';
import type { ImportFileEntry } from './importScreenState';
import type { ImportPoint } from './importTypes';
import { plannedDays, type ImportDay } from './importPayloads';

/** Imported days are walked, so they're matched on the hiking profile. */
export const IMPORT_MATCH_PROFILE = 'hike';

/**
 * Whether a file's days get matched: only a planned hike from the file's own lines. A hike
 * "from waypoints" is straight legs between far-apart points, which a matcher can't follow.
 */
export function snapsToPaths(entry: ImportFileEntry): boolean {
  return entry.settings.choice === 'planned' && entry.settings.snapToPaths === true;
}

/** Ticking the row: PRO users toggle it, everyone else is shown the upgrade page instead. */
export function snapToggleAction(isPro: boolean): 'toggle' | 'upgrade' {
  return isPro ? 'toggle' : 'upgrade';
}

/** What snapping did across one import, for the result line and the failure event. */
export interface SnapSummary {
  matched: number;
  failed: number;
  skipped: number;
  errors: string[];
}

export const emptySnapSummary = (): SnapSummary => ({ matched: 0, failed: 0, skipped: 0, errors: [] });

export function addSnapSummaries(a: SnapSummary, b: SnapSummary): SnapSummary {
  return {
    matched: a.matched + b.matched,
    failed: a.failed + b.failed,
    skipped: a.skipped + b.skipped,
    errors: [...a.errors, ...b.errors]
  };
}

/** A day as CreateHike saves it, plus the routed line string that replaces its plain one. */
export type SnappedHikeDay = HikeDay & { lineString: NonNullable<ReturnType<typeof lineStringSaveAttributes>> };

/**
 * A day rebuilt from its match: the matched line joined back onto the day's own start and end
 * (its markers), with GraphHopper's elevations, and the turns and surfaces saved as the planner
 * saves a routed segment. A bridge to a marker takes its neighbour's elevation, as the apps do.
 */
export function snappedDay(day: HikeDay, match: MapMatchResult): SnappedHikeDay | null {
  if ((match.graphooper.paths?.[0]?.points?.coordinates?.length ?? 0) < 2) return null;
  const feature = LineStringFeature.fromGraphooper(match.graphooper, IMPORT_MATCH_PROFILE);
  const matched = feature.lineStringGeometry.coordinates;
  if (!matched || matched.length < 2 || day.coordinates.length < 2) return null;

  const first = day.coordinates[0];
  const last = day.coordinates[day.coordinates.length - 1];
  const start = [first[0], first[1], matched[0][2] ?? 0];
  const end = [last[0], last[1], matched[matched.length - 1][2] ?? 0];
  const anchored = anchorToMarkers(feature, start, end);

  const lineString = lineStringSaveAttributes(anchored);
  if (!lineString) return null;
  const coordinates = anchored.lineStringGeometry.coordinates.map(c => [c[0], c[1], c[2] ?? 0]);
  return {
    title: day.title,
    coordinates,
    altitudes: coordinates.map(c => c[2]),
    distances: cumulativeDistancesMeters(coordinates),
    lineString
  };
}

/** Whether the import toast adds "Some days couldn't be snapped…": only when a day was left as drawn. */
export function showsSnapPartial(summary: SnapSummary | null | undefined): boolean {
  return !!summary && summary.failed > 0;
}

/**
 * Failures only, one event per import press, with the same name and fields the apps send to
 * BetterStack. The web has no client→BetterStack path, so it goes through the PostHog facade.
 */
export const MAP_MATCH_FAILED_EVENT = 'planner_map_match_failed';

export function mapMatchFailedProps(summary: SnapSummary): Record<string, string | number> | null {
  if (summary.failed === 0) return null;
  return {
    platform: 'web',
    source: 'import',
    segments_total: summary.matched + summary.failed + summary.skipped,
    matched: summary.matched,
    failed: summary.failed,
    skipped: summary.skipped,
    error: (summary.errors[0] || 'unknown').slice(0, 200)
  };
}

// ---- matching at tick time, for the preview ----------------------------------------------

/** One day's match: the stored GraphHopper answer and its line for the map, or why it failed. */
export type DayMatch =
  | { ok: true; match: MapMatchResult; line: ImportPoint[] }
  | { ok: false; skipped: boolean; error: string };

/** A file's matches, for the days `key` describes. */
export interface FileSnap {
  key: string;
  matching: boolean;
  days: DayMatch[];
}

/** The days a snapped file is matched as: a planned hike's days, as they'll be saved. */
export const snapDays = (entry: ImportFileEntry): ImportDay[] =>
  entry.preview ? plannedDays(entry.preview, entry.settings) : [];

/**
 * Names the days a match was made for, so a change to Days or Include course (which changes
 * them) shows up as a stale match that needs redoing.
 */
export function snapKey(days: ImportDay[]): string {
  const at = (p?: ImportPoint) => (p ? `${p.lon},${p.lat}` : '-');
  return days.map(d => `${d.points.length}:${at(d.points[0])}:${at(d.points[d.points.length - 1])}`).join('|');
}

/** Where a file's snap stands: `needed` means ticked but not (or no longer) matched. */
export type SnapStatus = 'off' | 'needed' | 'matching' | 'ready' | 'partial' | 'failed';

export function snapStatus(entry: ImportFileEntry, stored: FileSnap | undefined): SnapStatus {
  if (!snapsToPaths(entry) || !entry.preview) return 'off';
  if (!stored || stored.key !== snapKey(snapDays(entry))) return 'needed';
  if (stored.matching) return 'matching';
  const matched = stored.days.filter(d => d.ok).length;
  if (matched === 0) return 'failed';
  return stored.days.some(d => !d.ok && !d.skipped) ? 'partial' : 'ready';
}

/** The stored matches a save can use as they are, or null when there are none for these days. */
export function usableSnap(entry: ImportFileEntry, stored: FileSnap | undefined): FileSnap | null {
  const status = snapStatus(entry, stored);
  return status === 'ready' || status === 'partial' || status === 'failed' ? (stored as FileSnap) : null;
}

/** The matched line as the map draws it: joined onto the day's own start and end. */
export function matchedLine(points: ImportPoint[], match: MapMatchResult): ImportPoint[] | null {
  if (points.length < 2 || (match.graphooper.paths?.[0]?.points?.coordinates?.length ?? 0) < 2) return null;
  const feature = LineStringFeature.fromGraphooper(match.graphooper, IMPORT_MATCH_PROFILE);
  const first = points[0];
  const last = points[points.length - 1];
  const anchored = anchorToMarkers(feature, [first.lon, first.lat], [last.lon, last.lat]);
  return anchored.lineStringGeometry.coordinates.map(c => ({ lon: c[0], lat: c[1] }));
}

/**
 * Matches `days` one at a time. Stops and returns null as soon as `isCurrent` says the result is
 * no longer wanted (the file was unticked, removed or its days changed, or the dialog closed).
 */
export async function matchDays(
  days: ImportDay[],
  mapMatch: (coords: number[][], profile: string) => Promise<MapMatchResult>,
  isCurrent: () => boolean
): Promise<DayMatch[] | null> {
  const out: DayMatch[] = [];
  for (const day of days) {
    if (day.points.length < 2) {
      out.push({ ok: false, skipped: true, error: 'Too few points to match' });
      continue;
    }
    try {
      // One point every 50 m, and any detour the matcher makes re-asked without its cause.
      const match = await matchWithoutDetours(day.points.map(p => [p.lon, p.lat]), mapMatch, IMPORT_MATCH_PROFILE);
      const line = matchedLine(day.points, match);
      out.push(line ? { ok: true, match, line } : { ok: false, skipped: false, error: 'Map matching returned no line' });
    } catch (e) {
      out.push({ ok: false, skipped: false, error: e instanceof Error ? e.message : String(e) });
    }
    if (!isCurrent()) return null;
  }
  return isCurrent() ? out : null;
}

/**
 * Runs tasks one after another: every file's matching goes through one of these, so ticking
 * "Snap all to paths" over twenty files still sends GraphHopper one request at a time.
 */
export function serialQueue(): <T>(task: () => Promise<T>) => Promise<T> {
  let tail: Promise<unknown> = Promise.resolve();
  return <T>(task: () => Promise<T>): Promise<T> => {
    const run = tail.then(task, task);
    tail = run.catch(() => undefined);
    return run;
  };
}
